// src/components/MetadataCard.tsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    Chip,
    TextField,
    Autocomplete,
    MenuItem,
    Select,
    Stack,
} from '@mui/material';
import {
    fetchMetadata,
    updateMetadata,
    RecordMetadata,
    SEVERITY_LEVELS,
    fetchAllTags,
} from '../api/metadata';

type Props = {
    revisionGroupId: string;
};

const MetadataCard: React.FC<Props> = ({ revisionGroupId }) => {
    const [metadata, setMetadata] = useState<RecordMetadata | null>(null);
    const [allTags, setAllTags] = useState<string[]>([]);
    const [tagInput, setTagInput] = useState('');

    useEffect(() => {
        const load = async () => {
            const [data, globalTags] = await Promise.all([
                fetchMetadata(revisionGroupId),
                fetchAllTags(), // ⬅️ fetch global tags
            ]);
            setMetadata(data);
            // Merge local tags with global
            const mergedTags = Array.from(
                new Set([...(data.tags || []), ...globalTags])
            );
            setAllTags(mergedTags);
        };
        load();
    }, [revisionGroupId]);

    const handleSeverityChange = async (newSeverity: string | null) => {
        if (!metadata) return;
        const updated = await updateMetadata(revisionGroupId, {
            severity: newSeverity,
        });
        setMetadata(updated);
    };

    const handleTagDelete = async (tagToRemove: string) => {
        if (!metadata) return;
        const updated = await updateMetadata(revisionGroupId, {
            tags: metadata.tags.filter((tag) => tag !== tagToRemove),
        });
        setMetadata(updated);
    };

    const severityColors: Record<string, string> = {
        Unimportant: '#9ccc65', // green
        Low: '#66bb6a',
        Medium: '#ffa726',
        High: '#ef5350',
        Critical: '#d32f2f', // deep red
    };

    const handleAddTag = async (newTag: string) => {
        const cleaned = newTag.trim();
        if (!cleaned) return;

        // If tag already exists, do nothing but clear input
        if (metadata?.tags.includes(cleaned)) {
            setTagInput('');
            return;
        }

        const updated = await updateMetadata(revisionGroupId, {
            tags: [...(metadata?.tags ?? []), cleaned],
        });

        setMetadata(updated);
        setAllTags((prev) => Array.from(new Set([...prev, cleaned])));
        setTagInput('');
    };

    if (!metadata) return null;

    return (
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
            >
                {/* Severity Pill */}
                <Select
                    value={metadata.severity || ''}
                    displayEmpty
                    onChange={(e) =>
                        handleSeverityChange(e.target.value || null)
                    }
                    size="small"
                    sx={{
                        fontWeight: 500,
                        bgcolor:
                            severityColors[metadata.severity ?? ''] ||
                            'grey.200',
                        color: metadata.severity ? 'white' : 'black',
                        height: 28,
                        borderRadius: 5,
                        '& .MuiSelect-select': { py: 0.5, px: 2 },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                    }}
                >
                    <MenuItem value="">No Severity</MenuItem>
                    {SEVERITY_LEVELS.map((level) => (
                        <MenuItem key={level} value={level}>
                            {level}
                        </MenuItem>
                    ))}
                </Select>

                {/* Tags */}
                {metadata.tags.map((tag) => (
                    <Chip
                        key={tag}
                        label={tag}
                        onDelete={() => handleTagDelete(tag)}
                        size="small"
                        sx={{
                            bgcolor: 'grey.300',
                            color: 'black',
                            height: 28,
                            fontSize: '0.75rem',
                            px: 1,
                            borderRadius: 1.5,
                        }}
                    />
                ))}

                {/* Tag Autocomplete */}
                <Autocomplete
                    freeSolo
                    options={allTags.filter(
                        (tag) => !metadata.tags.includes(tag)
                    )}
                    inputValue={tagInput}
                    onInputChange={(_, value) => setTagInput(value)}
                    onChange={(_, value) => {
                        if (typeof value === 'string') handleAddTag(value);
                    }}
                    value={null}
                    sx={{ minWidth: 120 }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Add tag"
                            size="small"
                            sx={{
                                height: 28,
                                fontSize: '0.75rem',
                                '& .MuiOutlinedInput-root': {
                                    height: 28,
                                    px: 1,
                                    borderRadius: 1.5,
                                    fontSize: '0.75rem',
                                },
                                '& .MuiInputBase-input': {
                                    py: 0.5,
                                    px: 1,
                                },
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddTag(tagInput); // handles duplicate case inside
                                }
                            }}
                        />
                    )}
                    componentsProps={{
                        popper: {
                            modifiers: [
                                { name: 'offset', options: { offset: [0, 4] } },
                            ],
                            sx: {
                                '& .MuiAutocomplete-listbox': {
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                },
                                '& .MuiAutocomplete-option': {
                                    minHeight: '32px',
                                    padding: '4px 8px',
                                },
                            },
                        },
                    }}
                />
            </Stack>
        </Box>
    );
};

export default MetadataCard;
